import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { t } from 'i18next';
// contexts
import { useAlert } from 'context';
// components
import { useToast } from '@commons/EXComponentsLibrary';
// apis
import { getCommercialAgreementsWithPagination } from 'api';
import { deleteAgreementById } from 'api/api-agreements';
// utils && hooks
import { useDebounce, useUser } from 'utils/hooks';

const useAgreementList = ({ setWithoutAgreements }) => {
    const { query, replace } = useRouter();
    const toaster = useToast();
    const { show } = useAlert();
    const { userLocaleCountryCode } = useUser({});

    const [searchValue, setSearchValue] = useState<string>('');
    const [data, setData] = useState<any>([]);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFirstFetch, setIsFirstFetch] = useState<boolean>(true);
    const [totalRows, setTotalRows] = useState<number>(20);
    const [perPage, setPerPage] = useState<number>(20);

    const searchValueDebounced = useDebounce(searchValue, 500);
    const withoutAgreement = !searchValue && data?.length === 0 && !isLoading;

    const handleClearSearch = () => {
        setSearchValue('');
        handleGetAgreements(20, 1);
    };

    const handleGetAgreements = async (rows: number = 20, page: number = 1, search?: string) => {
        setIsLoading(true);
        let res;

        if (!!search) {
            res = await getCommercialAgreementsWithPagination(rows, page, search);
        } else {
            res = await getCommercialAgreementsWithPagination(rows, page);
        }

        if (res?.data?.meta?.pagination?.total_pages) {
            const { current_page, count, per_page, total, total_pages } = res.data.meta.pagination;
            setCurrentPage(current_page);
            setTotalRows(count);
            setTotalPages(total_pages);
            setPerPage(per_page);
        }

        setData(res.data.data);
        if (isFirstFetch) setIsFirstFetch(false);
        setIsLoading(false);
    };

    useEffect(() => {
        // handleGetAgreements();
        if (query?.showAlert && ['create', 'update'].includes(query?.showAlert as string)) {
            toaster.showToaster({
                message: {
                    title:
                        query?.showAlert === 'create'
                            ? t('settings.commercialAgreements.sett_successFullTextcommercialAgreement')
                            : t('settings.editPointsOfSale.sett_saveChangesSuccessfullTextPointsOfSale'),
                },
                duration: 5000,
                type: 'success',
            });

            replace(
                {
                    pathname: `/${query?.clientId}/settings/trade-agreements`,
                },
                undefined,
                { shallow: true },
            );
        }
    }, []);

    useEffect(() => {
        if (!isLoading) {
            handleGetAgreements(20, 1, searchValueDebounced);
        }
    }, [searchValueDebounced]);

    useEffect(() => {
        setWithoutAgreements(withoutAgreement && !isFirstFetch);
    }, [withoutAgreement]);

    const handleDeleteAgreement = (id: number) => {
        show({
            className: '',
            cancel: true,
            size: 'xs',
            title: 'Eliminar acuerdo',
            body: 'Se eliminará el acuerdo.\n\nEsta acción no se puede revertir.',
            action: 'Eliminar',
            onConfirm: () => {
                deleteAgreementById(id)
                    .then(() => handleGetAgreements(20, 1))
                    .then(() =>
                        toaster.showToaster({
                            message: {
                                title: t('settings.commercialAgreements.sett_showMessageSuccess'),
                            },
                            duration: 5000,
                            type: 'success',
                        }),
                    )
                    .catch((err) =>
                        toaster.showToaster({
                            message: {
                                title: 'Ocurrió un error en el sistema. Intente más tarde',
                            },
                            duration: 5000,
                            type: 'error',
                        }),
                    );
            },
            onCancel: () => {},
        });
    };

    return {
        handleClearSearch,
        handleGetAgreements,
        searchValue,
        setSearchValue,
        data,
        perPage,
        isLoading,
        isFirstFetch,
        totalRows,
        currentPage,
        totalPages,
        userLocaleCountryCode,
        handleDeleteAgreement,
        withoutAgreement,
    };
};

export default useAgreementList;
